import React, { useState } from 'react';
import { CheckCircle2Icon, ClockIcon, MailIcon, MapPinIcon, MessageCircleIcon, PhoneIcon, SendIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { BUSINESS } from '../data/i18n';
import { services } from '../data/services';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
interface FormState {
  name: string;
  phone: string;
  service: string;
  address: string;
  message: string;
}
type Errors = Partial<Record<keyof FormState, string>>;
const empty: FormState = {
  name: '',
  phone: '',
  service: '',
  address: '',
  message: ''
};
export function ContactForm() {
  const {
    t
  } = useLanguage();
  const [form, setForm] = useState<FormState>(empty);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const update = (field: keyof FormState, value: string) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };
  const validate = (): Errors => {
    const next: Errors = {};
    if (form.name.trim().length < 3) next.name = t('contact.err.name');
    if (form.phone.replace(/\D/g, '').length < 10) next.phone = t('contact.err.phone');
    if (!form.service) next.service = t('contact.err.service');
    if (form.address.trim().length < 5) next.address = t('contact.err.address');
    return next;
  };
  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 700);
  };
  const fieldClass = (hasError: boolean) => `w-full rounded-md border bg-white px-4 py-3 text-[15px] text-navy-900 placeholder:text-navy-900/40 transition-colors duration-150 ease-smooth focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20 ${hasError ? 'border-red-500' : 'border-navy-900/15'}`;
  return <section id="contact" className="w-full bg-navy-900 py-20 sm:py-24" data-id="element-28" data-mp="2wylipa9yc0ap">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:px-8" data-id="element-29" data-mp="2rww6hraztgl1">
        <Reveal data-id="element-30" data-mp="35v2t2bahru5e">
          <div data-id="element-31" data-mp="30y2n00dn6rea">
            <SectionHeading eyebrow={t('contact.eyebrow')} title={t('contact.title')} description={t('contact.sub')} tone="light" data-id="element-32" data-mp="30xkn8trrtv1m" />

            <div className="mt-8 rounded-2xl bg-white p-6 shadow-lift sm:p-8" data-id="element-33" data-mp="297g1aqod4zya">
              {status === 'sent' ? <div className="flex flex-col items-start gap-4 py-6" data-id="element-34" data-mp="2mzcpwkqtorpx">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-forest-600/10 text-forest-600" data-id="element-35" data-mp="2i72varerj3hb">
                    <CheckCircle2Icon className="h-6 w-6" aria-hidden="true" data-id="element-36" data-mp="1ugyuersylhu0" />
                  </span>
                  <h3 className="font-display text-xl font-extrabold text-navy-900" data-id="element-37" data-mp="1pi5nxjc3ar3c">
                    {t('contact.success.title')}
                  </h3>
                  <p className="max-w-md text-[15px] leading-relaxed text-navy-900/70" data-id="element-38" data-mp="9rcvzn2tfn9f">
                    {t('contact.success.body')}
                  </p>
                  <button type="button" onClick={() => {
                setForm(empty);
                setStatus('idle');
              }} className="mt-1 text-sm font-bold text-brand-600 hover:text-amber-600" data-id="element-39" data-mp="3s7fddt2338c7">
                    {t('contact.success.again')}
                  </button>
                </div> : <form onSubmit={onSubmit} noValidate className="grid grid-cols-1 gap-5 sm:grid-cols-2" data-id="element-40" data-mp="et26n1m4lywz">
                  <div data-id="element-41" data-mp="2zgwzdwztnmk0">
                    <label htmlFor="name" className="mb-1.5 block text-sm font-bold text-navy-900" data-id="element-42" data-mp="2zgdqf5s5g8i4">
                      {t('contact.name')}
                    </label>
                    <input id="name" name="name" value={form.name} onChange={e => update('name', e.target.value)} placeholder={t('contact.namePh')} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className={fieldClass(Boolean(errors.name))} data-id="element-43" data-mp="3jr7wex3vs0df" />
                    {errors.name ? <p id="name-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-44" data-mp="2fp0cpus8oqw">
                        {errors.name}
                      </p> : null}
                  </div>

                  <div data-id="element-45" data-mp="2i8o12l38rp0t">
                    <label htmlFor="phone" className="mb-1.5 block text-sm font-bold text-navy-900" data-id="element-46" data-mp="2i8xr28oadw6i">
                      {t('contact.phone')}
                    </label>
                    <input id="phone" name="phone" type="tel" value={form.phone} onChange={e => update('phone', e.target.value)} placeholder={t('contact.phonePh')} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} className={fieldClass(Boolean(errors.phone))} data-id="element-47" data-mp="2377b95pskl0m" />
                    {errors.phone ? <p id="phone-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-48" data-mp="2vu1tdvz4z8ax">
                        {errors.phone}
                      </p> : null}
                  </div>

                  <div data-id="element-49" data-mp="11hlslap40mdg">
                    <label htmlFor="service" className="mb-1.5 block text-sm font-bold text-navy-900" data-id="element-50" data-mp="11i5346cfk8hg">
                      {t('contact.service')}
                    </label>
                    <select id="service" name="service" value={form.service} onChange={e => update('service', e.target.value)} aria-invalid={Boolean(errors.service)} aria-describedby={errors.service ? 'service-error' : undefined} className={fieldClass(Boolean(errors.service))} data-id="element-51" data-mp="tg4ervur86tx">
                      <option value="" data-id="element-52" data-mp="1ce6zvzk6ljmg">{t('contact.select')}</option>
                      {services.map(service => <option key={service.id} value={service.id} data-id="element-53" data-mp="1m49z4hhwbtt8">
                          {t(service.titleKey)}
                        </option>)}
                      <option value="other" data-id="element-54" data-mp="204azzwy5v18p">{t('contact.other')}</option>
                    </select>
                    {errors.service ? <p id="service-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-55" data-mp="2eq8e1gsj4x2j">
                        {errors.service}
                      </p> : null}
                  </div>

                  <div data-id="element-56" data-mp="25p0fqyq0jw8g">
                    <label htmlFor="address" className="mb-1.5 block text-sm font-bold text-navy-900" data-id="element-57" data-mp="25pjwic3xgekw">
                      {t('contact.address')}
                    </label>
                    <input id="address" name="address" value={form.address} onChange={e => update('address', e.target.value)} placeholder={t('contact.addressPh')} aria-invalid={Boolean(errors.address)} aria-describedby={errors.address ? 'address-error' : undefined} className={fieldClass(Boolean(errors.address))} data-id="element-58" data-mp="20qz0kovpkh29" />
                    {errors.address ? <p id="address-error" className="mt-1.5 text-xs font-semibold text-red-600" data-id="element-59" data-mp="1xhz1p0z46z0c">
                        {errors.address}
                      </p> : null}
                  </div>

                  <div className="sm:col-span-2" data-id="element-60" data-mp="1ojx3et6rmkgr">
                    <label htmlFor="message" className="mb-1.5 block text-sm font-bold text-navy-900" data-id="element-61" data-mp="1jjiu77tfh4h5">
                      {t('contact.message')}{' '}
                      <span className="font-medium text-navy-900/45" data-id="element-62" data-mp="19s4r6q29pmj9">
                        ({t('contact.optional')})
                      </span>
                    </label>
                    <textarea id="message" name="message" rows={4} value={form.message} onChange={e => update('message', e.target.value)} placeholder={t('contact.messagePh')} className={`${fieldClass(false)} resize-y`} data-id="element-63" data-mp="10t15debput12" />
                  </div>

                  <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row" data-id="element-64" data-mp="37f75udj64trd">
                    <button type="submit" disabled={status === 'sending'} className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-amber-600 px-6 py-4 font-display text-sm font-bold text-white transition-[background-color,transform] duration-150 ease-smooth hover:bg-amber-700 active:scale-[0.99] disabled:opacity-70" data-id="element-65" data-mp="3cfbly2g5zmh2">
                      <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-66" data-mp="2sq170qo8t5k0" />
                      {status === 'sending' ? t('contact.sending') : t('contact.submit')}
                    </button>
                    <a href={BUSINESS.whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-whats-500 px-6 py-4 font-display text-sm font-bold text-white transition-[background-color,transform] duration-150 ease-smooth hover:bg-whats-600 active:scale-[0.99]" data-id="element-67" data-mp="20zwtnbywhisa">
                      <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-68" data-mp="3ti85p12bipr3" />
                      {t('contact.whatsapp')}
                    </a>
                  </div>
                </form>}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.08} data-id="element-69" data-mp="30u2kkd7cqyqq">
          <div className="lg:pt-24" data-id="element-70" data-mp="35sw0eca431u2">
            <h3 className="font-display text-lg font-extrabold text-white" data-id="element-71" data-mp="35rumm01po18n">
              {t('contact.infoTitle')}
            </h3>
            <ul className="mt-6 flex flex-col gap-6" data-id="element-72" data-mp="3akx7ljsfa2d8">
              <li className="flex items-start gap-4" data-id="element-73" data-mp="2d505nd43rjzx">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-amber-400" data-id="element-74" data-mp="2d25hbahyxf18">
                  <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-75" data-mp="2ajjyk1ts3v5v" />
                </span>
                <a href={BUSINESS.phoneHref} className="font-display text-xl font-extrabold text-white hover:text-amber-400" data-id="element-76" data-mp="25kpg50zq48ma">
                  +1 {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li className="flex items-start gap-4" data-id="element-77" data-mp="1wjrcijt76348">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-amber-400" data-id="element-78" data-mp="1rlg50fqdk4p6">
                  <MailIcon className="h-5 w-5" aria-hidden="true" data-id="element-79" data-mp="1mtfy23bw0bj3" />
                </span>
                <a href={`mailto:${BUSINESS.email}`} className="break-all text-[15px] font-semibold text-white/85 hover:text-amber-400" data-id="element-80" data-mp="1hw6a6s45c7xd">
                  {BUSINESS.email}
                </a>
              </li>
              <li className="flex items-start gap-4" data-id="element-81" data-mp="13vlrdvy8ec3v">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-amber-400" data-id="element-82" data-mp="13vvi5lr3onam">
                  <MapPinIcon className="h-5 w-5" aria-hidden="true" data-id="element-83" data-mp="ua42e0xibc7u" />
                </span>
                <span data-id="element-84" data-mp="y78hbst40qoc">
                  <span className="block text-xs font-bold uppercase tracking-wide text-white/50" data-id="element-85" data-mp="k9tmg8k37qoz">
                    {t('contact.areaLabel')}
                  </span>
                  <span className="mt-0.5 block text-[15px] font-semibold text-white/85" data-id="element-86" data-mp="par4ml53kkqh">
                    {BUSINESS.area}
                  </span>
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-87" data-mp="1hwiup05duzja">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/10 text-amber-400" data-id="element-88" data-mp="1397lv60l5tmv">
                  <ClockIcon className="h-5 w-5" aria-hidden="true" data-id="element-89" data-mp="1867qdchseobv" />
                </span>
                <span data-id="element-90" data-mp="1vwbj2otft3o3">
                  <span className="block text-xs font-bold uppercase tracking-wide text-white/50" data-id="element-91" data-mp="1vvsdm7kz47qy">
                    {t('contact.hoursLabel')}
                  </span>
                  <span className="mt-0.5 block text-[15px] font-semibold text-white/85" data-id="element-92" data-mp="3ld756olkjrhx">
                    {t('contact.hours1')}
                  </span>
                  <span className="block text-[15px] font-semibold text-white/85" data-id="element-93" data-mp="3u6j0z4b3w1v5">
                    {t('contact.hours2')}
                  </span>
                  <span className="block text-[15px] text-white/55" data-id="element-94" data-mp="3q68jaudybn0g">{t('contact.hours3')}</span>
                </span>
              </li>
            </ul>
          </div>
        </Reveal>
      </div>
    </section>;
}